<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FollowUpStepsList from 'dashboard/components-next/Campaigns/Advanced/FollowUp/FollowUpStepsList.vue';
import FollowUpStepFormDialog from 'dashboard/components-next/Campaigns/Advanced/FollowUp/FollowUpStepFormDialog.vue';
import FollowUpFormDialog from 'dashboard/components-next/Campaigns/Advanced/FollowUp/FollowUpFormDialog.vue';
import EnrollContactsDialog from 'dashboard/components-next/Campaigns/Advanced/FollowUp/EnrollContactsDialog.vue';

import FollowUpSequencesAPI from 'dashboard/api/followUpSequences';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const sequenceId = computed(() => route.params.sequenceId);
const sequence = ref(null);
const steps = ref([]);
const stats = ref(null);
const isLoading = ref(false);
const isLoadingSteps = ref(false);

// Enrolled contacts
const enrolledContacts = ref([]);
const isLoadingEnrolled = ref(false);

// Dialog refs
const stepFormRef = ref(null);
const enrollDialogRef = ref(null);
const sequenceFormRef = ref(null);
const editingStep = ref(null);

const fetchSequence = async () => {
  isLoading.value = true;
  try {
    const response = await FollowUpSequencesAPI.show(sequenceId.value);
    sequence.value = response.data;
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.DETAIL.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const fetchSteps = async () => {
  isLoadingSteps.value = true;
  try {
    const response = await FollowUpSequencesAPI.getSteps(sequenceId.value);
    steps.value = response.data || [];
  } catch {
    steps.value = [];
  } finally {
    isLoadingSteps.value = false;
  }
};

const fetchStats = async () => {
  try {
    const response = await FollowUpSequencesAPI.getStats(sequenceId.value);
    stats.value = response.data;
  } catch {
    stats.value = null;
  }
};

const fetchEnrolledContacts = async () => {
  isLoadingEnrolled.value = true;
  try {
    const response = await FollowUpSequencesAPI.getEnrolledContacts(
      sequenceId.value
    );
    enrolledContacts.value = response.data || [];
  } catch {
    enrolledContacts.value = [];
  } finally {
    isLoadingEnrolled.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'campaigns_follow_up' });
};

const handleToggleActive = async () => {
  if (!sequence.value) return;
  try {
    await FollowUpSequencesAPI.update(sequenceId.value, {
      active: !sequence.value.active,
    });
    sequence.value.active = !sequence.value.active;
    useAlert(t('CAMPAIGN.FOLLOW_UP.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.UPDATE_ERROR'));
  }
};

const handleDelete = async () => {
  if (!window.confirm(t('CAMPAIGN.FOLLOW_UP.DELETE_CONFIRM'))) return;
  try {
    await FollowUpSequencesAPI.delete(sequenceId.value);
    useAlert(t('CAMPAIGN.FOLLOW_UP.DELETE_SUCCESS'));
    goBack();
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.DELETE_ERROR'));
  }
};

// Edit sequence
const handleEditSequence = () => {
  sequenceFormRef.value?.open();
};

const handleSaveSequence = async payload => {
  try {
    payload.id = sequenceId.value;
    await FollowUpSequencesAPI.update(sequenceId.value, payload);
    useAlert(t('CAMPAIGN.FOLLOW_UP.UPDATE_SUCCESS'));
    fetchSequence();
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.UPDATE_ERROR'));
  }
};

// Step actions
const handleAddStep = () => {
  editingStep.value = null;
  stepFormRef.value?.open();
};

const handleEditStep = step => {
  editingStep.value = step;
  stepFormRef.value?.open();
};

const handleDeleteStep = async step => {
  if (!window.confirm(t('CAMPAIGN.FOLLOW_UP.STEP.DELETE_CONFIRM'))) return;
  try {
    await FollowUpSequencesAPI.deleteStep(sequenceId.value, step.id);
    useAlert(t('CAMPAIGN.FOLLOW_UP.STEP.DELETE_SUCCESS'));
    fetchSteps();
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.STEP.DELETE_ERROR'));
  }
};

const handleSaveStep = async payload => {
  try {
    if (payload.id) {
      await FollowUpSequencesAPI.updateStep(
        sequenceId.value,
        payload.id,
        payload
      );
      useAlert(t('CAMPAIGN.FOLLOW_UP.STEP.UPDATE_SUCCESS'));
    } else {
      await FollowUpSequencesAPI.createStep(sequenceId.value, payload);
      useAlert(t('CAMPAIGN.FOLLOW_UP.STEP.CREATE_SUCCESS'));
    }
    fetchSteps();
  } catch {
    useAlert(
      payload.id
        ? t('CAMPAIGN.FOLLOW_UP.STEP.UPDATE_ERROR')
        : t('CAMPAIGN.FOLLOW_UP.STEP.CREATE_ERROR')
    );
  }
};

// Enroll
const handleOpenEnroll = () => {
  enrollDialogRef.value?.open();
};

const handleEnrollComplete = () => {
  fetchStats();
  fetchEnrolledContacts();
};

// Cancel enrollment
const handleCancelContact = async contact => {
  if (!window.confirm(t('CAMPAIGN.FOLLOW_UP.DETAIL.CANCEL_CONFIRM'))) return;
  try {
    await FollowUpSequencesAPI.cancelContact(
      sequenceId.value,
      contact.id,
      'Cancelled by admin'
    );
    useAlert(t('CAMPAIGN.FOLLOW_UP.DETAIL.CANCEL_SUCCESS'));
    fetchEnrolledContacts();
    fetchStats();
  } catch {
    useAlert(t('CAMPAIGN.FOLLOW_UP.DETAIL.CANCEL_ERROR'));
  }
};

const triggerLabel = computed(() => {
  if (!sequence.value) return '';
  const key = (sequence.value.trigger_event || 'manual').toUpperCase();
  return t(`CAMPAIGN.FOLLOW_UP.TRIGGER.${key}`);
});

const formatPercent = value => {
  if (!value) return '0%';
  return `${value.toFixed(1)}%`;
};

onMounted(() => {
  fetchSequence();
  fetchSteps();
  fetchStats();
  fetchEnrolledContacts();
});
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <header class="px-6 py-4 border-b border-n-weak">
      <div class="flex items-center gap-3">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          @click="goBack"
        >
          {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.BACK') }}
        </Button>
      </div>
    </header>

    <Spinner v-if="isLoading" class="m-auto" />

    <div v-else-if="sequence" class="flex-1 overflow-y-auto p-6 space-y-6">
      <!-- Sequence info -->
      <div class="flex items-start justify-between">
        <div>
          <div class="flex items-center gap-3">
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ sequence.name }}
            </h1>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                sequence.active
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-alpha-2 text-n-slate-10'
              "
            >
              {{
                sequence.active
                  ? t('CAMPAIGN.FOLLOW_UP.ACTIVE')
                  : t('CAMPAIGN.FOLLOW_UP.INACTIVE')
              }}
            </span>
          </div>
          <p v-if="sequence.description" class="mt-1 text-sm text-n-slate-11">
            {{ sequence.description }}
          </p>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ triggerLabel }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-pencil"
            @click="handleEditSequence"
          >
            {{ t('CAMPAIGN.FOLLOW_UP.EDIT_BTN') }}
          </Button>
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-user-plus"
            @click="handleOpenEnroll"
          >
            {{ t('CAMPAIGN.FOLLOW_UP.ENROLL_BTN') }}
          </Button>
          <Button
            variant="faded"
            size="sm"
            :icon="sequence.active ? 'i-lucide-pause' : 'i-lucide-play'"
            @click="handleToggleActive"
          >
            {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.TOGGLE_ACTIVE') }}
          </Button>
          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash"
            @click="handleDelete"
          />
        </div>
      </div>

      <!-- Stats -->
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div class="rounded-lg border border-n-weak p-4">
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.ENROLLED_CONTACTS') }}
          </p>
          <p class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ stats?.enrolled || sequence.total_enrolled || 0 }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak p-4">
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.COMPLETED_CONTACTS') }}
          </p>
          <p class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ stats?.completed || sequence.total_completed || 0 }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak p-4">
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.FOLLOW_UP.COMPLETION_RATE') }}
          </p>
          <p class="mt-1 text-2xl font-semibold text-n-blue-11">
            {{
              formatPercent(stats?.completion_rate || sequence.completion_rate)
            }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak p-4">
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.FOLLOW_UP.CONVERSION_RATE') }}
          </p>
          <p class="mt-1 text-2xl font-semibold text-n-teal-11">
            {{
              formatPercent(stats?.conversion_rate || sequence.conversion_rate)
            }}
          </p>
        </div>
      </div>

      <!-- Stop conditions -->
      <div class="flex flex-wrap items-center gap-3 text-xs text-n-slate-10">
        <span v-if="sequence.stop_on_reply" class="flex items-center gap-1">
          <span class="i-lucide-message-circle w-3 h-3" />
          {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_REPLY') }}
        </span>
        <span v-if="sequence.stop_on_click" class="flex items-center gap-1">
          <span class="i-lucide-mouse-pointer-click w-3 h-3" />
          {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_CLICK') }}
        </span>
        <span v-if="sequence.stop_on_optout" class="flex items-center gap-1">
          <span class="i-lucide-ban w-3 h-3" />
          {{ t('CAMPAIGN.FOLLOW_UP.STOP_ON_OPTOUT') }}
        </span>
      </div>

      <!-- Steps timeline -->
      <FollowUpStepsList
        :steps="steps"
        :is-loading="isLoadingSteps"
        @add="handleAddStep"
        @edit="handleEditStep"
        @delete="handleDeleteStep"
      />

      <!-- Enrolled contacts -->
      <div v-if="enrolledContacts.length > 0" class="space-y-3">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.ENROLLED_CONTACTS') }}
        </h3>
        <div class="rounded-lg border border-n-weak overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-n-alpha-2">
              <tr>
                <th
                  class="px-4 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.CONTACT_NAME') }}
                </th>
                <th
                  class="px-4 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.CONTACT_PHONE') }}
                </th>
                <th
                  class="px-4 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.CONTACT_STATUS') }}
                </th>
                <th
                  class="px-4 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  {{ t('CAMPAIGN.FOLLOW_UP.DETAIL.CONTACT_STEP') }}
                </th>
                <th
                  class="px-4 py-2 text-right text-xs font-medium text-n-slate-10"
                />
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="contact in enrolledContacts"
                :key="contact.id"
                class="hover:bg-n-alpha-1"
              >
                <td class="px-4 py-2.5 text-n-slate-12">
                  {{ contact.name || '-' }}
                </td>
                <td class="px-4 py-2.5 text-n-slate-11">
                  {{ contact.phone_number || '-' }}
                </td>
                <td class="px-4 py-2.5">
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full"
                    :class="{
                      'bg-n-teal-3 text-n-teal-11':
                        contact.execution_status === 'sent',
                      'bg-n-amber-3 text-n-amber-11':
                        contact.execution_status === 'scheduled' ||
                        contact.execution_status === 'pending',
                      'bg-n-ruby-3 text-n-ruby-11':
                        contact.execution_status === 'failed',
                      'bg-n-alpha-2 text-n-slate-10':
                        contact.execution_status === 'cancelled',
                    }"
                  >
                    {{ contact.execution_status }}
                  </span>
                </td>
                <td class="px-4 py-2.5 text-n-slate-11">
                  {{ contact.current_step || '-' }}
                </td>
                <td class="px-4 py-2.5 text-right">
                  <Button
                    v-if="
                      contact.execution_status !== 'cancelled' &&
                      contact.execution_status !== 'sent'
                    "
                    size="tiny"
                    variant="faded"
                    color="ruby"
                    icon="i-lucide-x"
                    @click="handleCancelContact(contact)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Step form dialog -->
    <FollowUpStepFormDialog
      ref="stepFormRef"
      :step="editingStep"
      :next-order="steps.length + 1"
      @save="handleSaveStep"
    />

    <!-- Edit sequence dialog -->
    <FollowUpFormDialog
      ref="sequenceFormRef"
      :sequence="sequence"
      @save="handleSaveSequence"
    />

    <!-- Enroll contacts dialog -->
    <EnrollContactsDialog
      ref="enrollDialogRef"
      :sequence-id="sequenceId"
      @enrolled="handleEnrollComplete"
    />
  </div>
</template>
